import _ from 'lodash';
const useCurrencyHelpers = ({ locale = 'es-MX', maximumFractionDigits = 2, currency = 'MXN' }) => {
	const formatter = new Intl.NumberFormat(locale, {
		style: 'currency',
		currency,
		minimumFractionDigits: 0,
		maximumFractionDigits
	});

	const toParts = formatter.formatToParts(4);
	const currencySymbol = toParts.find(({ type }) => type === 'currency')?.value || '';
	const thousandSeparator =
		formatter.formatToParts(11111111).find(({ type }) => type === 'group')?.value || ',';
	const decimalSeparator =
		formatter.formatToParts(1.1).find(({ type }) => type === 'decimal')?.value || '.';
	const literalSeparator =
		formatter.formatToParts(1.1).find(({ type }) => type === 'literal')?.value || '';

	const parseToNumberBeforeSubmit = (stringNumber: string) => {
		if (typeof stringNumber === 'number') {
			return stringNumber;
		}

		if (_.isEmpty(stringNumber)) {
			return 0;
		}

		return parseFloat(
			stringNumber
				.replace(currencySymbol, '')
				.replace(literalSeparator, '')
				.replace(new RegExp(`\\${thousandSeparator}`, 'g'), '')
				.replace(new RegExp(`\\${decimalSeparator}`), '.')
		);
	};

	const parseToMoneyBeforeSubmit = (number: string) => {
		if (_.isEmpty(number)) {
			return '0.00 MXN';
		}
		const moneyFormat = `${parseToNumberBeforeSubmit(number).toFixed(2)} ${currency}`;

		return moneyFormat;
	};

	const trimExceedingDecimals = (inputString: string) => {
		const [integer, decimals = ''] = inputString.split(decimalSeparator);
		const trimmedDecimals = decimals.slice(0, maximumFractionDigits);
		return [integer, trimmedDecimals].join(decimalSeparator);
	};

	const sanitizeInput = (input: string) => {
		let cleaned = '';
		let hasDecimalSeparator = false;

		_.each(`${input}`, (letter) => {
			//allowing only one separator
			if (letter === decimalSeparator && !hasDecimalSeparator) {
				cleaned += letter;
				hasDecimalSeparator = true;
				return;
			}

			if (_.includes(['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'], letter)) {
				cleaned += letter;
			}
		});

		if (hasDecimalSeparator) {
			return trimExceedingDecimals(cleaned);
		}

		return cleaned;
	};

	const normalizeToMaxLength = (inputString: string) => {
		const sanitized = sanitizeInput(inputString);
		const trimmedExceedingDecimals = trimExceedingDecimals(sanitized);
		return _.endsWith(trimmedExceedingDecimals, decimalSeparator)
			? trimmedExceedingDecimals.slice(0, -1)
			: trimmedExceedingDecimals;
	};

	const parseToCleanString = (value: string) => {
		if (value === formatter.format(0)) {
			return '';
		}

		if (_.isNumber(value)) {
			return `${value}`.replace('.', decimalSeparator);
		}

		if (_.isEmpty(value)) {
			return '';
		}

		if (_.isEqual(value, currencySymbol)) {
			return '';
		}

		if (_.isUndefined(value)) {
			return '';
		}

		return normalizeToMaxLength(value);
	};

	const format = (value: string) => {
		if (_.isEmpty(value)) {
			return formatter.format(0);
		}

		const normalizedToMaxLength = normalizeToMaxLength(value);
		return formatter.format(parseToNumberBeforeSubmit(normalizedToMaxLength));
	};

	return {
		parseToCleanString,
		format,
		formatter,
		sanitizeInput,
		parseToNumberBeforeSubmit,
		parseToMoneyBeforeSubmit
	};
};

export default useCurrencyHelpers;
